import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodGetB2bContractsDocumentsFilters = z.object({
  contract_id: ZodId,
});

export type GetB2bContractsDocumentsFilters = z.TypeOf<typeof ZodGetB2bContractsDocumentsFilters>;

export const B2bContractsDocumentsDocumentTypeValues = [
  'employee_savings_booklet',
  'general_terms_and_conditions',
  'plan_regulations',
  'amendments',
  'price_list_employee',
  'price_list_company',
  'signed_contract_amendment',
] as const;

const ZodB2bContractsDocumentsType = z.enum(B2bContractsDocumentsDocumentTypeValues);

export type B2bContractsDocumentsType = z.TypeOf<typeof ZodB2bContractsDocumentsType>;

const ZodGetB2bContractsDocumentsResponseOne = z.object({
  id: ZodId,
  contract_id: ZodId,
  name: ZodTrimmedString,
  url: ZodTrimmedString,
  type: ZodB2bContractsDocumentsType,
  created_at: z.date(),
  updated_at: z.date(),
});

export type GetB2bContractsDocumentsResponseOne = z.TypeOf<
  typeof ZodGetB2bContractsDocumentsResponseOne
>;

export const ZodGetB2bContractsDocumentsResponse = z.array(ZodGetB2bContractsDocumentsResponseOne);

export type GetB2bContractsDocumentsResponse = z.TypeOf<typeof ZodGetB2bContractsDocumentsResponse>;

export const getB2bContractsDocumentsSortColumns: (keyof GetB2bContractsDocumentsResponseOne)[] = [
  'id',
  'name',
  'url',
  'created_at',
  'type',
];

const ZodB2bGetContractsDocumentsQuery = buildZodBoListQueryParsed(
  ZodGetB2bContractsDocumentsFilters,
  getB2bContractsDocumentsSortColumns,
  { range: [0, 10] },
);
export type GetB2bContractsDocumentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bContractsDocumentsFilters>,
  (typeof getB2bContractsDocumentsSortColumns)[number]
>;

export const getB2bContractsDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bGetContractsDocumentsQuery,
  responseBody: ZodGetB2bContractsDocumentsResponse,
});

export type GetB2bContractsDocumentsEndpoint = GetEndpointType<
  typeof getB2bContractsDocumentsEndpoint
>;

const ZodPostB2bContractsDocumentRequest = z.object({
  contract_id: ZodId,
  type: ZodB2bContractsDocumentsType,
  presigned_path: ZodTrimmedString,
  name: ZodTrimmedString,
});

export type PostB2bContractsDocumentRequest = z.TypeOf<typeof ZodPostB2bContractsDocumentRequest>;

export const postB2bContractsDocumentsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bContractsDocumentRequest,
  responseBody: ZodGetB2bContractsDocumentsResponseOne,
});

export type PostB2bContractsDocumentsEndpoint = GetEndpointType<
  typeof postB2bContractsDocumentsEndpoint
>;

export const ZodPutB2bContractsDocumentsIdParams = createZodParamsValidator(
  z.object({
    id: ZodId,
  }),
);

export const ZodPutB2bContractsDocumentsIdRequestBody = z.object({
  name: ZodTrimmedString,
});

export const ZodPutB2bContractsDocumentsIdResponseBody = z.object({
  id: ZodId,
  name: ZodTrimmedString,
});

export const putB2bContractsDocumentsIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodPutB2bContractsDocumentsIdParams,
  body: ZodPutB2bContractsDocumentsIdRequestBody,
  responseBody: ZodPutB2bContractsDocumentsIdResponseBody,
});

export type PutB2bContractsDocumentsIdEndpoint = GetEndpointType<
  typeof putB2bContractsDocumentsIdEndpoint
>;

export const getB2bContractsDocumentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bContractsDocumentsResponseOne,
});

export type GetB2bContractsDocumentsIdEndpoint = GetEndpointType<
  typeof getB2bContractsDocumentsIdEndpoint
>;
